/**
 * Selector de mes y año
 * -----------------------------------------------------------------------------
 * MDB no tiene un selector de mes: su datepicker maneja vistas internas de mes
 * y año, pero elegir un mes siempre baja al detalle de días. Este componente
 * cubre los dos casos que faltan:
 *
 *   - `mode="card"`  -> MM/AA,   solo desde el mes actual hacia adelante.
 *                       Caducidad de tarjetas.
 *   - `mode="month"` -> MM/AAAA, cualquier año (por defecto).
 *                       Filtros de reportes, períodos de nómina, cierres.
 *
 * Uso:
 *   <div class="form-outline" data-company-month-init data-company-month-mode="card">
 *     <input type="text" class="form-control" id="venc" />
 *     <label class="form-label" for="venc">Vencimiento</label>
 *   </div>
 *
 * Opciones por atributo: `data-company-month-mode`, `-min-year`, `-max-year`.
 */

import { monthPickerES } from './locale-es.js';
import { placePanel, closeOnViewportChange } from './place-panel.js';

const CARD_YEARS_AHEAD = 15;
const MONTH_YEARS_BACK = 10;
const MONTH_YEARS_AHEAD = 10;

let uid = 0;

function pad(value) {
  return String(value).padStart(2, '0');
}

/**
 * Formatea {year, month} según el modo. `month` es 1-12.
 */
function format(mode, year, month) {
  return mode === 'card' ? `${pad(month)}/${String(year).slice(-2)}` : `${pad(month)}/${year}`;
}

/**
 * Lee el valor escrito en el input. Devuelve null si no encaja con el modo.
 * En modo tarjeta, un año de dos cifras se interpreta en el siglo actual.
 */
function parse(mode, value) {
  if (!value) return null;

  const match = value.trim().match(/^(\d{1,2})\s*[/\-.]\s*(\d{2}|\d{4})$/);
  if (!match) return null;

  const month = Number(match[1]);
  if (month < 1 || month > 12) return null;

  let year = Number(match[2]);
  if (match[2].length === 2) year += Math.floor(new Date().getFullYear() / 100) * 100;

  return { year, month };
}

function readConfig(wrapper) {
  const mode = wrapper.getAttribute('data-company-month-mode') === 'card' ? 'card' : 'month';
  const now = new Date();
  const currentYear = now.getFullYear();

  const minYear = Number(
    wrapper.getAttribute('data-company-month-min-year') ||
      (mode === 'card' ? currentYear : currentYear - MONTH_YEARS_BACK)
  );

  const maxYear = Number(
    wrapper.getAttribute('data-company-month-max-year') ||
      (mode === 'card' ? currentYear + CARD_YEARS_AHEAD : currentYear + MONTH_YEARS_AHEAD)
  );

  return { mode, minYear, maxYear };
}

/**
 * En modo tarjeta no tiene sentido ofrecer un mes ya vencido.
 */
function isDisabled(config, year, month) {
  if (config.mode !== 'card') return false;

  const now = new Date();
  return year === now.getFullYear() && month < now.getMonth() + 1;
}

class MonthPicker {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.input = wrapper.querySelector('input');
    this.config = readConfig(wrapper);
    this.panel = null;
    this.selected = parse(this.config.mode, this.input.value);

    const now = new Date();
    this.viewYear = (this.selected && this.selected.year) || now.getFullYear();

    uid += 1;
    this.id = `company-month-${uid}`;

    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);

    this._setup();
  }

  _setup() {
    this.wrapper.classList.add('company-month');
    // El panel se posiciona respecto al campo.
    if (getComputedStyle(this.wrapper).position === 'static') {
      this.wrapper.style.position = 'relative';
    }

    this.input.setAttribute('inputmode', 'none');
    this.input.setAttribute('autocomplete', 'off');
    this.input.style.cursor = 'pointer';
    this.input.setAttribute('aria-haspopup', 'dialog');

    this.input.addEventListener('click', () => this.toggle());
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.toggle();
      }
    });

    // Aunque se elige por el panel, en escritorio se puede teclear. Sin filtro
    // entraba cualquier cosa —«hola», «99/99»— y al no encajar el formato se
    // ignoraba en silencio, dejando el texto inválido a la vista.
    this.input.setAttribute('maxlength', this.config.mode === 'card' ? '5' : '7');

    this.input.addEventListener('input', (event) => this._mask(event));
    this.input.addEventListener('blur', () => this._commit());

    this.input.addEventListener('change', () => this._commit());
  }

  /**
   * Deja solo lo que puede ser una fecha: dígitos, la barra en su sitio y un
   * mes entre 01 y 12.
   *
   * El mes se corrige mientras se escribe: un primer dígito mayor que 1 solo
   * puede ser un mes de un dígito, así que «5» pasa a «05» y el campo salta
   * solo al año. Es lo que se espera al teclear un vencimiento de memoria.
   */
  _mask(event) {
    const raw = this.input.value;
    const borrando = event && String(event.inputType || '').startsWith('delete');

    const maxDigits = this.config.mode === 'card' ? 4 : 6;
    const digits = raw.replace(/\D/g, '').slice(0, maxDigits);

    let mes = digits.slice(0, 2);
    const anio = digits.slice(2);

    if (mes.length === 1 && Number(mes) > 1) mes = `0${mes}`;

    if (mes.length === 2) {
      const n = Number(mes);
      if (n === 0) mes = '01';
      else if (n > 12) mes = '12';
    }

    let out = mes;

    // La barra se añade sola al completar el mes, pero NO al borrar: si no, no
    // habría forma de retroceder más allá de ella.
    if (mes.length === 2 && (anio.length > 0 || !borrando)) out += `/${anio}`;

    if (out !== raw) {
      this.input.value = out;
      // Campo de cinco caracteres: el cursor va siempre al final, que es donde
      // sigue escribiendo quien teclea una fecha.
      this.input.setSelectionRange(out.length, out.length);
    }
  }

  /**
   * Al salir del campo: o hay una fecha válida, o no queda nada.
   *
   * Dejar «03/» a medias sería peor que vaciarlo: parece rellenado y no lo
   * está, y el formulario lo daría por bueno si el campo no es obligatorio.
   */
  _commit() {
    // `_select()` emite `change` al elegir en el panel, y esto se ejecuta desde
    // ese evento: sin la marca se llamaría a sí mismo.
    if (this._committing) return;
    this._committing = true;

    try {
      const parsed = parse(this.config.mode, this.input.value);

      if (!parsed) {
        // Sin fecha no hay nada que marcar en rojo: la marca de una anterior se
        // quita siempre, o el campo se queda en error estando vacío.
        this.wrapper.classList.remove('is-invalid');
        if (!this.input.value) return;

        this.input.value = '';
        this.input.classList.remove('active'); // el label vuelve a su sitio
        this.selected = null;
        return;
      }

      // Una tarjeta vencida no sirve para cobrar, y el panel ya no deja elegir
      // esos meses: se rechaza también al teclearlos.
      if (this._isPast(parsed)) {
        this.wrapper.classList.add('is-invalid');
        return;
      }

      this.wrapper.classList.remove('is-invalid');
      this.selected = parsed;
      this.viewYear = parsed.year;

      // Se normaliza lo tecleado: «3/9» queda «03/29».
      this.input.value = format(this.config.mode, parsed.year, parsed.month);
    } finally {
      this._committing = false;
    }
  }

  /** Meses de años ya pasados, que `isDisabled` no cubre. */
  _isPast(parsed) {
    if (this.config.mode !== 'card') return false;

    const now = new Date();
    if (parsed.year < now.getFullYear()) return true;

    return parsed.year === now.getFullYear() && parsed.month < now.getMonth() + 1;
  }

  get isOpen() {
    return !!this.panel;
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  open() {
    if (this.isOpen) return;

    this.panel = document.createElement('div');
    this.panel.className = 'company-month-panel';
    this.panel.id = this.id;
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-label', monthPickerES.title);

    this._render();
    // Al body, no dentro del campo: ver el porqué en place-panel.js.
    document.body.appendChild(this.panel);
    placePanel(this.wrapper, this.panel);

    this._stopWatching = closeOnViewportChange(() => this.close(), this.panel);
    document.addEventListener('click', this._onDocumentClick, true);
    document.addEventListener('keydown', this._onKeydown);
  }

  close() {
    if (!this.isOpen) return;

    this.panel.remove();
    this.panel = null;

    if (this._stopWatching) {
      this._stopWatching();
      this._stopWatching = null;
    }

    document.removeEventListener('click', this._onDocumentClick, true);
    document.removeEventListener('keydown', this._onKeydown);
  }

  _onDocumentClick(event) {
    if (!this.panel) return;
    if (this.panel.contains(event.target) || event.target === this.input) return;
    this.close();
  }

  _onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  _render() {
    const { minYear, maxYear } = this.config;
    const canPrev = this.viewYear > minYear;
    const canNext = this.viewYear < maxYear;

    this.panel.innerHTML = `
      <div class="company-month-header">
        <button type="button" class="company-month-nav" data-step="-1"
          aria-label="${monthPickerES.prevYear}" ${canPrev ? '' : 'disabled'}>
          <i class="fas fa-chevron-left"></i>
        </button>
        <span class="company-month-year">${this.viewYear}</span>
        <button type="button" class="company-month-nav" data-step="1"
          aria-label="${monthPickerES.nextYear}" ${canNext ? '' : 'disabled'}>
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
      <div class="company-month-grid">
        ${monthPickerES.monthsShort
          .map((name, index) => {
            const month = index + 1;
            const disabled = isDisabled(this.config, this.viewYear, month);
            const active =
              this.selected &&
              this.selected.year === this.viewYear &&
              this.selected.month === month;

            return `<button type="button" class="company-month-cell${active ? ' active' : ''}"
              data-month="${month}" ${disabled ? 'disabled' : ''}>${name}</button>`;
          })
          .join('')}
      </div>
      <div class="company-month-footer">
        <button type="button" class="company-month-btn" data-action="clear">${monthPickerES.clear}</button>
        <button type="button" class="company-month-btn" data-action="current">${monthPickerES.current}</button>
      </div>
    `;

    this.panel.querySelectorAll('.company-month-nav').forEach((button) => {
      button.addEventListener('click', () => {
        this.viewYear += Number(button.dataset.step);
        this._render();
      });
    });

    this.panel.querySelectorAll('.company-month-cell').forEach((button) => {
      button.addEventListener('click', () => {
        this._select(this.viewYear, Number(button.dataset.month));
      });
    });

    this.panel.querySelector('[data-action="clear"]').addEventListener('click', () => {
      this.selected = null;
      this.input.value = '';
      this.input.classList.remove('active');
      this._emit();
      this.close();
    });

    this.panel.querySelector('[data-action="current"]').addEventListener('click', () => {
      const now = new Date();
      this._select(now.getFullYear(), now.getMonth() + 1);
    });

    // Al cambiar de año el alto puede variar: se recoloca si ya está abierto.
    if (this.panel.isConnected) placePanel(this.wrapper, this.panel);
  }

  _select(year, month) {
    const { minYear, maxYear } = this.config;
    if (year < minYear || year > maxYear || isDisabled(this.config, year, month)) return;

    this.selected = { year, month };
    this.viewYear = year;
    this.input.value = format(this.config.mode, year, month);
    this.input.classList.add('active');

    this._emit();
    this.close();
  }

  _emit() {
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.wrapper.dispatchEvent(
      new CustomEvent('valueChanged.company.monthpicker', {
        bubbles: true,
        detail: { value: this.input.value, selected: this.selected },
      })
    );
  }

  dispose() {
    this.close();
    this.wrapper.classList.remove('company-month');
    delete this.wrapper.__companyMonthPicker;
  }

  static getInstance(wrapper) {
    return wrapper.__companyMonthPicker || null;
  }
}

/**
 * Inicializa los selectores de mes presentes en `root`.
 *
 * @param {ParentNode} [root=document] Ámbito a recorrer.
 */
export function setupMonthPickers(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('[data-company-month-init]').forEach((wrapper) => {
    if (wrapper.__companyMonthPicker) return;
    if (!wrapper.querySelector('input')) return;

    wrapper.__companyMonthPicker = new MonthPicker(wrapper);
  });
}

export { MonthPicker };
export default setupMonthPickers;
